// useInactivityTimeout.js
// Logs out user after specified inactivity period
// Tracks mouse, keyboard, touch, and scroll events

import { useEffect, useRef, useCallback } from "react";
import { supabase } from "./supabase";

const INACTIVITY_MS = 60 * 60 * 1000; // 1 hour
const WARNING_MS   = 5  * 60 * 1000;  // warn 5 min before logout

export function useInactivityTimeout({ user, onWarning, onLogout }) {
  const logoutTimer  = useRef(null);
  const warningTimer = useRef(null);
  const warned       = useRef(false);

  const resetTimers = useCallback(() => {
    // Clear existing timers
    if (logoutTimer.current)  clearTimeout(logoutTimer.current);
    if (warningTimer.current) clearTimeout(warningTimer.current);
    warned.current = false;

    if (!user) return;

    // Warning at 55 minutes
    warningTimer.current = setTimeout(() => {
      if (!warned.current) {
        warned.current = true;
        onWarning && onWarning();
      }
    }, INACTIVITY_MS - WARNING_MS);

    // Logout at 60 minutes
    logoutTimer.current = setTimeout(async () => {
      await supabase.auth.signOut();
      onLogout && onLogout();
    }, INACTIVITY_MS);
  }, [user, onWarning, onLogout]);

  useEffect(() => {
    if (!user) return;

    // Activity events to track
    const events = ["mousedown", "mousemove", "keydown", "touchstart", "scroll", "click"];
    
    const handleActivity = () => resetTimers();

    events.forEach(e => window.addEventListener(e, handleActivity, { passive: true }));
    
    // Start timers immediately
    resetTimers();

    return () => {
      events.forEach(e => window.removeEventListener(e, handleActivity));
      if (logoutTimer.current)  clearTimeout(logoutTimer.current);
      if (warningTimer.current) clearTimeout(warningTimer.current);
    };
  }, [user, resetTimers]);
}
